/**
 * Visão de lista dos pedidos unificados (redesign admin, Fase 2): filtros de
 * tela e agregados de painel. Puro e sem I/O.
 *
 * Os filtros aqui são SÓ de apresentação (busca, canal, estado) sobre o recorte
 * que a página já decidiu (`?kpi=`/`?checkout=` continuam sendo resolvidos pelos
 * seletores do dataset — esta camada nunca recalcula o recorte do indicador).
 * Os agregados contam o que existe: nenhuma série histórica é inventada.
 */
import { acquisitionOriginKey } from "./acquisition.js";
import {
  ORDER_ORIGINS,
  ORDER_ORIGIN_LABELS,
  ORDER_STATES,
  ORDER_STATE_LABELS,
  isRevenueState,
  type PlatformOrderOrigin,
  type PlatformOrderState
} from "./state-machine.js";
import type { PlatformUnifiedOrder } from "../api/types.js";
import { salesChannelKey } from "./channels.js";
import { itemsSearchText } from "./products.js";

export interface OrderFilters {
  /** Busca por id, identificador no canal ou nome do cliente (sem acento/caixa). */
  query: string;
  origin: PlatformOrderOrigin | "todos";
  /** Canal de venda real (mercado_livre, shopee, ...); ver `orders/channels.ts`. */
  channel?: string;
  /** Origem de aquisição (slug, `unset` = sem origem, `todos` = sem filtro); ver `orders/acquisition.ts`. */
  acquisition?: string;
  state: PlatformOrderState | "todos";
}

export const EMPTY_ORDER_FILTERS: OrderFilters = { query: "", origin: "todos", channel: "todos", acquisition: "todos", state: "todos" };

function fold(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

export function filterOrders(orders: PlatformUnifiedOrder[], filters: OrderFilters): PlatformUnifiedOrder[] {
  const query = fold(filters.query);
  return orders.filter((order) => {
    if (filters.origin !== "todos" && order.origin !== filters.origin) return false;
    if (filters.channel && filters.channel !== "todos" && salesChannelKey(order) !== filters.channel) return false;
    if (filters.acquisition && filters.acquisition !== "todos" && acquisitionOriginKey(order) !== filters.acquisition) return false;
    if (filters.state !== "todos" && order.state !== filters.state) return false;
    if (!query) return true;
    // Busca por número, cliente, situação do Bling e PRODUTO (descrição ou SKU dos itens).
    return [order.id, order.externalId, order.customer?.name ?? "", order.blingSituation ?? "", order.acquisitionStrategy ?? "", order.responsibleName ?? "", itemsSearchText(order)].some((value) => fold(value).includes(query));
  });
}

export interface OrderBucket<K extends string> {
  key: K;
  label: string;
  count: number;
  /** Soma de `totalCents` dos pedidos que contam como receita (estados de receita). */
  revenueCents: number;
}

/** Quantos pedidos em cada estado; estados sem pedido ficam de fora. */
export function stateBuckets(orders: PlatformUnifiedOrder[]): Array<OrderBucket<PlatformOrderState>> {
  return ORDER_STATES.map((state) => {
    const rows = orders.filter((order) => order.state === state);
    return {
      key: state,
      label: ORDER_STATE_LABELS[state],
      count: rows.length,
      revenueCents: revenueOf(rows)
    };
  }).filter((bucket) => bucket.count > 0);
}

/** Quantos pedidos e quanta receita por canal; canais sem pedido ficam de fora. */
export function channelBuckets(orders: PlatformUnifiedOrder[]): Array<OrderBucket<PlatformOrderOrigin>> {
  return ORDER_ORIGINS.map((origin) => {
    const rows = orders.filter((order) => order.origin === origin);
    return {
      key: origin,
      label: ORDER_ORIGIN_LABELS[origin],
      count: rows.length,
      revenueCents: revenueOf(rows)
    };
  }).filter((bucket) => bucket.count > 0);
}

function revenueOf(orders: PlatformUnifiedOrder[]): number {
  return orders.reduce((sum, order) => (isRevenueState(order.state) ? sum + (order.totalCents ?? 0) : sum), 0);
}

/** Receita (estados de receita), pedidos de receita e ticket médio — `null` sem pedidos de receita. */
export function revenueSummary(orders: PlatformUnifiedOrder[]): {
  revenueCents: number;
  revenueOrders: number;
  averageTicketCents: number | null;
} {
  const rows = orders.filter((order) => isRevenueState(order.state) && order.totalCents !== null);
  const revenueCents = rows.reduce((sum, order) => sum + (order.totalCents ?? 0), 0);
  return {
    revenueCents,
    revenueOrders: rows.length,
    averageTicketCents: rows.length ? Math.round(revenueCents / rows.length) : null
  };
}

/**
 * Pedidos em andamento ("em aberto agora"): da cobrança até o faturamento.
 * `delivered` (venda concluída, só admite devolução) e os desfeitos
 * (`cancelled`/`returned`) não são fila de trabalho.
 */
const CLOSED_STATES: readonly PlatformOrderState[] = ["delivered", "cancelled", "returned"];
export function openOrders(orders: PlatformUnifiedOrder[]): PlatformUnifiedOrder[] {
  return orders.filter((order) => !CLOSED_STATES.includes(order.state));
}
